/**
 * ReportingLinePanel — inline reporting-line (manager) change for one seat's
 * incumbent, driven through the SAME saga API HR-Ops uses.
 *
 * Mutation: `employment` with a single `fields_after.LineManager` wire value in
 * F&O's canonical form `[PN] Last, First`. There is NO effective date — the
 * saga re-points the in-force PositionHierarchies edge immediately and mirrors
 * REPORTSTOEMP into Sage.
 *
 * Two targets:
 *   - a PERSON: an active worker holding an active position (the picker filters
 *     to active workers); the seat's Line edge goes to their seat and they
 *     become the Sage Report To.
 *   - a VACANT POSITION (asked for at the 6 Oct 2026 structure session:
 *     reporting lines follow the structure, vacancies included). The Line edge
 *     goes to the vacant seat (`options.line_position_id`) and the person acting
 *     for it is sent as LineManager, so they become the Sage Report To. Once F&O
 *     has the line, that person is recorded as this seat's absentee approver,
 *     so leave goes to the same person.
 *
 * A recorded absentee approver always wins over the line for leave, so moving
 * a seat back to a person offers to clear it.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, GitBranch, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
  integrationService,
  type EmployeeData,
} from '@/services/api/integrationService';
import organizationService, { type Position } from '@/services/api/organizationService';
import { AsyncSearchCombobox } from './AsyncSearchCombobox';
import { MutationSagaStatus } from './MutationSagaStatus';
import { useMutationSaga } from '@/hooks/useMutationSaga';
import { useOccupancyRefresh } from '@/hooks/useOccupancyRefresh';

type WireSource = Pick<EmployeeData, 'personnel_number' | 'full_name' | 'first_name' | 'last_name'>;

// F&O LineManager wire value: `[PN] Last, First`. Prefer the structured
// first/last from the directory row; fall back to splitting full_name so we
// never emit `[PN] , ` for HR-only rows that lack the split fields.
function lineManagerWireValue(e: WireSource): string {
  let first = e.first_name?.trim() || '';
  let last = e.last_name?.trim() || '';
  if (!first || !last) {
    const parts = (e.full_name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length >= 2) {
      if (!last) last = parts[parts.length - 1];
      if (!first) first = parts.slice(0, -1).join(' ');
    } else {
      if (!first) first = e.full_name?.trim() || '';
    }
  }
  return `[${e.personnel_number}] ${last}, ${first}`.replace(/\s+$/, '');
}

// Some seats carry a platform code ("FO-005557") that F&O doesn't know: the
// F&O id is fo_position_id. Numeric codes are the F&O id.
const foId = (p: Pick<Position, 'position_code' | 'fo_position_id'>) =>
  p.fo_position_id || p.position_code;

/** The person acting for a vacant position. */
interface StandIn {
  pn: string;
  name: string;
  /** F&O id of the stand-in's own seat: what the absentee approver records. */
  seat: string;
  title: string;
  /** Why the panel suggested them, when they weren't picked by hand. */
  suggestedBecause?: string;
}

/** What has to happen once F&O has the new line. */
interface FollowUp {
  /** The absentee approver to record on this seat; null clears it. */
  approver: string | null;
  reason?: string;
}

async function standInFromSeat(
  positionId: number,
  incumbentPn: string,
  suggestedBecause?: string,
): Promise<StandIn> {
  const d = await organizationService.getPosition(positionId);
  const pn = d.incumbent?.employee_id?.trim() || '';
  const name = (d.incumbent?.display_name || d.incumbent_name || '').trim();
  if (!pn || !name) {
    throw new Error(
      `${d.title} (${d.position_code}) has nobody in it with a personnel number. Pick another position.`,
    );
  }
  if (pn === incumbentPn) {
    throw new Error(`${name} can't act for their own line. Pick someone else.`);
  }
  return { pn, name, seat: foId(d), title: d.title, suggestedBecause };
}

export interface ReportingLinePanelProps {
  incumbentPn: string;
  incumbentName?: string;
  currentManagerName?: string;
  /**
   * The drawer seat's F&O position code — the reporting-line change re-points
   * THIS seat's hierarchy edge, so it's the seat whose occupancy we re-read
   * post-commit. Omit to fall back to the nightly-sync projection.
   */
  seatPositionCode?: string;
  /** The seat's F&O id, when its platform code differs (an "FO-" code). */
  seatFoPositionId?: string;
  /** The seat's recorded absentee approver, if any (it outranks the line for leave). */
  recordedStandIn?: { name?: string | null } | null;
  onDone: () => void;
  /** HR-Ops fallback deep-link + opener (used on a 403). */
  hrOpsUrl?: string | null;
  onOpenHrOps?: (url: string) => void;
}

export const ReportingLinePanel: React.FC<ReportingLinePanelProps> = ({
  incumbentPn,
  incumbentName,
  currentManagerName,
  seatPositionCode,
  seatFoPositionId,
  recordedStandIn,
  onDone,
  hrOpsUrl,
  onOpenHrOps,
}) => {
  const who = incumbentName ?? incumbentPn;
  // The absentee approver is recorded on this seat, so the vacant option needs it.
  const seatFo = seatFoPositionId || seatPositionCode || '';
  const [target, setTarget] = useState<'person' | 'vacant'>('person');
  const [manager, setManager] = useState<EmployeeData | null>(null);
  const [clearStandIn, setClearStandIn] = useState(true);
  const [vacantSeat, setVacantSeat] = useState<Position | null>(null);
  // Why the chosen vacant seat can't take this line (it reports to this seat).
  const [vacantProblem, setVacantProblem] = useState<string | null>(null);
  const [standIn, setStandIn] = useState<StandIn | null>(null);
  const [standInLoading, setStandInLoading] = useState(false);
  const [standInError, setStandInError] = useState<string | null>(null);
  const [after, setAfter] = useState<{ state: 'idle' | 'running' | 'ok' | 'error'; error?: string }>({
    state: 'idle',
  });
  // A later pick supersedes an earlier lookup still in flight.
  const lookup = useRef(0);
  const followUp = useRef<FollowUp | null>(null);
  const saga = useMutationSaga();

  const sagaDone = saga.phase === 'completed' || saga.phase === 'partial';
  // F&O re-points this seat's PositionHierarchies edge, so re-read this seat.
  const refresh = useOccupancyRefresh(
    sagaDone,
    seatPositionCode ? [seatPositionCode] : [],
  );

  // F&O has the line ('partial' = only Sage failed): now record, or clear,
  // this seat's absentee approver. Once per submit.
  useEffect(() => {
    const job = followUp.current;
    if (!sagaDone || !job) return;
    followUp.current = null;
    setAfter({ state: 'running' });
    organizationService
      .setAbsenteeApprover(seatFo, job.approver, job.reason)
      .then((res) =>
        setAfter(
          res.ok
            ? { state: 'ok' }
            : { state: 'error', error: res.error || res.edge_error || 'it was not recorded' },
        ),
      )
      .catch((e) => setAfter({ state: 'error', error: e instanceof Error ? e.message : String(e) }));
  }, [sagaDone, seatFo]);

  const searchManagers = useMemo(
    () => async (q: string) => {
      const res = await integrationService.searchEmployees(q, 20, {
        status: 'active',
        includeHrOnly: true,
      });
      // A worker cannot report to themselves.
      return (res.employees ?? []).filter(
        (e) => e.personnel_number !== incumbentPn,
      );
    },
    [incumbentPn],
  );

  const searchVacantSeats = useMemo(
    () => async (q: string) => {
      // Live seats only: never point a line at a RETIRED / FROZEN / PENDING seat.
      const positions = await organizationService.listPositions({
        query: q,
        limit: 20,
        status: ['ACTIVE', 'VACANT'],
      });
      return positions.filter((p) => !p.incumbent_name && p.position_code !== seatPositionCode);
    },
    [seatPositionCode],
  );

  // Same lookup as the absentee-approver editor: a FILLED seat, never this one.
  const searchStandIns = useMemo(
    () => async (q: string) => {
      const positions = await organizationService.listPositions({ query: q, limit: 20, status: ['ACTIVE'] });
      return positions.filter((p) => !!p.incumbent_name && p.position_code !== seatPositionCode);
    },
    [seatPositionCode],
  );

  const pickStandIn = async (p: Position) => {
    const ticket = ++lookup.current;
    setStandIn(null);
    setStandInError(null);
    setStandInLoading(true);
    try {
      const s = await standInFromSeat(p.id, incumbentPn);
      if (ticket === lookup.current) setStandIn(s);
    } catch (e) {
      if (ticket === lookup.current) setStandInError(e instanceof Error ? e.message : String(e));
    } finally {
      if (ticket === lookup.current) setStandInLoading(false);
    }
  };

  // Suggest who acts for the vacant seat: whoever already acts for its other
  // reports, else the person the vacant seat itself reports to.
  const pickVacantSeat = async (v: Position) => {
    const ticket = ++lookup.current;
    setVacantSeat(v);
    setVacantProblem(null);
    setStandIn(null);
    setStandInError(null);
    setStandInLoading(true);
    try {
      const d = await organizationService.getPosition(v.id);
      if (ticket !== lookup.current) return;
      if (d.reports_to?.position_code && d.reports_to.position_code === seatPositionCode) {
        setVacantProblem(
          `${d.title} reports to ${who}’s own position, so the line would go round in a circle. Pick another position.`,
        );
        return;
      }
      const parent = d.reports_to?.incumbent_name && d.reports_to.status !== 'VACANT' ? d.reports_to : null;
      const candidate = d.acting_for_reports
        ? { id: d.acting_for_reports.id, because: "already acts for the position's other reports" }
        : parent
          ? { id: parent.id, because: 'the vacant position reports to them' }
          : null;
      if (!candidate) return;
      const s = await standInFromSeat(candidate.id, incumbentPn, candidate.because);
      if (ticket === lookup.current) setStandIn(s);
    } catch {
      // Only a suggestion: the picker below still works.
    } finally {
      if (ticket === lookup.current) setStandInLoading(false);
    }
  };

  const canSubmit =
    !saga.isBusy &&
    (target === 'person'
      ? !!manager
      : !!vacantSeat && !vacantProblem && !!standIn && !standInLoading);

  const submit = () => {
    if (!canSubmit) return;
    setAfter({ state: 'idle' });
    if (target === 'person') {
      if (!manager) return;
      followUp.current = recordedStandIn && clearStandIn ? { approver: null } : null;
      saga.submit(incumbentPn, {
        mutation_type: 'employment',
        fields_after: { LineManager: lineManagerWireValue(manager) },
        reason: `Reporting-line change for ${who}: manager set to ${manager.full_name} (${manager.personnel_number})`,
      });
      return;
    }
    if (!vacantSeat || !standIn) return;
    const code = foId(vacantSeat);
    // Shown in the drawer next to the absentee approver: plain words.
    followUp.current = {
      approver: standIn.seat,
      reason: `${vacantSeat.title} (${code}) is vacant; ${standIn.name} acts for it until it is filled.`,
    };
    saga.submit(incumbentPn, {
      mutation_type: 'employment',
      fields_after: {
        LineManager: lineManagerWireValue({ personnel_number: standIn.pn, full_name: standIn.name }),
      },
      options: { line_position_id: code },
      reason: `Reporting-line change for ${who}: reports to the vacant position ${vacantSeat.title} (${code}); ${standIn.name} (${standIn.pn}) acts for it`,
    });
  };

  // ── Success ──────────────────────────────────────────────────────────────
  if (sagaDone) {
    const partial = saga.phase === 'partial';
    const vacant = target === 'vacant';
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 rounded-md border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="font-medium">
              {partial
                ? 'F&O updated — Sage needs follow-up'
                : 'Reporting line updated'}
            </p>
            <p className="mt-0.5">
              {partial
                ? 'The manager change was written to F&O, but the Sage side reported an issue (see below). Payroll will need a manual follow-up.'
                : vacant
                  ? `Reports to the vacant position ${vacantSeat?.title ?? '—'}. ${standIn?.name ?? '—'} acts for it: the Report To in Sage and, for leave, the absentee approver.`
                  : `Manager set to ${manager?.full_name ?? '—'}.`}
            </p>
            <p className="mt-1.5 text-xs opacity-90">
              {refresh.state === 'ok'
                ? 'F&O updated — the reporting line here is refreshed.'
                : refresh.state === 'error'
                  ? 'F&O updated — the org chart / occupancy here refreshes after the next nightly sync.'
                  : 'F&O updated — refreshing the reporting line here…'}
            </p>
            {after.state === 'running' && (
              <p className="mt-1.5 flex items-center gap-1.5 text-xs opacity-90">
                <Loader2 className="h-3 w-3 animate-spin" />
                {vacant
                  ? `Recording ${standIn?.name ?? 'the stand-in'} as absentee approver…`
                  : 'Clearing the recorded absentee approver…'}
              </p>
            )}
            {after.state === 'ok' && !vacant && (
              <p className="mt-1.5 text-xs opacity-90">
                The recorded absentee approver is cleared: leave follows the new manager.
              </p>
            )}
          </div>
        </div>

        {after.state === 'error' && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-300">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <p>
              {vacant
                ? `The reporting line changed, but ${standIn?.name ?? 'the stand-in'} could not be recorded as absentee approver (${after.error}). Set it under Absentee approver on this position; until then leave goes to the next filled position above.`
                : `The reporting line changed, but the recorded absentee approver could not be cleared (${after.error}). Clear it under Absentee approver on this position; until then leave still goes to them.`}
            </p>
          </div>
        )}

        <MutationSagaStatus
          phase={saga.phase}
          mutation={saga.mutation}
          error={saga.error}
          errorStatus={saga.errorStatus}
          hrOpsUrl={hrOpsUrl}
          onOpenHrOps={onOpenHrOps}
        />

        <Button
          type="button"
          className="w-full sm:w-auto"
          disabled={after.state === 'running'}
          onClick={async () => {
            // Let the targeted re-read land before the drawer refetches.
            await refresh.settle();
            onDone();
          }}
        >
          Done
        </Button>
      </div>
    );
  }

  // ── Form ─────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-heritage-blue/10 p-2 text-heritage-blue dark:bg-heritage-blue-400/20 dark:text-heritage-blue-100">
          <GitBranch className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">
            Change reporting line
          </h3>
          <p className="mt-0.5 text-sm text-muted-foreground">
            Re-point {who}’s manager. Takes effect
            immediately in F&amp;O — no effective date.
          </p>
        </div>
      </div>

      {currentManagerName && (
        <p className="text-sm text-muted-foreground">
          Current manager:{' '}
          <span className="font-medium text-foreground">{currentManagerName}</span>
        </p>
      )}

      {seatFo && (
        <div className="space-y-1.5">
          <Label>Reports to</Label>
          <RadioGroup
            value={target}
            onValueChange={(v) => setTarget(v === 'vacant' ? 'vacant' : 'person')}
            className="flex flex-wrap gap-4"
            disabled={saga.isBusy}
          >
            <label className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="person" /> A person
            </label>
            <label className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="vacant" /> A vacant position
            </label>
          </RadioGroup>
        </div>
      )}

      {target === 'person' ? (
        <>
          <div className="space-y-1.5">
            <Label>New manager</Label>
            <AsyncSearchCombobox<EmployeeData>
              search={searchManagers}
              getKey={(e) => e.personnel_number}
              getPrimary={(e) => e.full_name}
              getSecondary={(e) =>
                [e.personnel_number, e.worker_status].filter(Boolean).join(' · ')
              }
              onSelect={setManager}
              placeholder="Search active workers by name…"
              disabled={saga.isBusy}
              emptyText="No active workers match."
            />
            {manager && (
              <p className="text-xs text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{manager.full_name}</span>{' '}
                ({manager.personnel_number})
              </p>
            )}
          </div>
          {recordedStandIn && (
            <label className="flex items-start gap-2 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={clearStandIn}
                disabled={saga.isBusy}
                onCheckedChange={(c) => setClearStandIn(c === true)}
              />
              <span>
                Clear the recorded absentee approver
                {recordedStandIn.name ? ` (${recordedStandIn.name})` : ''}, so leave follows the new manager
              </span>
            </label>
          )}
        </>
      ) : (
        <>
          <div className="space-y-1.5">
            <Label>Vacant position</Label>
            <AsyncSearchCombobox<Position>
              search={searchVacantSeats}
              getKey={(p) => p.position_code}
              getPrimary={(p) => p.title}
              getSecondary={(p) => [p.position_code, p.department].filter(Boolean).join(' · ')}
              onSelect={(p) => { void pickVacantSeat(p); }}
              placeholder="Search vacant positions by title or code…"
              disabled={saga.isBusy}
              emptyText="No vacant positions match."
            />
            {vacantSeat && (
              <p className="text-xs text-muted-foreground">
                Selected: <span className="font-medium text-foreground">{vacantSeat.title}</span>{' '}
                ({vacantSeat.position_code})
              </p>
            )}
            {vacantProblem && <p className="text-xs text-destructive">{vacantProblem}</p>}
          </div>

          {vacantSeat && !vacantProblem && (
            <div className="space-y-1.5">
              <Label>Acting for it</Label>
              <p className="text-xs text-muted-foreground">
                Until the position is filled, this person is {who}’s Report To in Sage and approves their leave.
              </p>
              <AsyncSearchCombobox<Position>
                search={searchStandIns}
                getKey={(p) => p.position_code}
                getPrimary={(p) => p.incumbent_name || 'Vacant'}
                getSecondary={(p) => `${p.title} · ${p.position_code}`}
                onSelect={(p) => { void pickStandIn(p); }}
                placeholder="Search active workers by name…"
                disabled={saga.isBusy}
                emptyText="No filled positions match."
              />
              {standInLoading && (
                <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Loader2 className="h-3 w-3 animate-spin" /> Looking up who acts for it…
                </p>
              )}
              {standIn && (
                <p className="text-xs text-muted-foreground">
                  {standIn.suggestedBecause ? 'Suggested' : 'Selected'}:{' '}
                  <span className="font-medium text-foreground">{standIn.name}</span> ({standIn.title} · {standIn.pn})
                  {standIn.suggestedBecause ? `, ${standIn.suggestedBecause}` : ''}
                </p>
              )}
              {standInError && <p className="text-xs text-destructive">{standInError}</p>}
            </div>
          )}
        </>
      )}

      <MutationSagaStatus
        phase={saga.phase}
        mutation={saga.mutation}
        error={saga.error}
        errorStatus={saga.errorStatus}
        hrOpsUrl={hrOpsUrl}
        onOpenHrOps={onOpenHrOps}
      />

      <Button
        type="button"
        className="w-full sm:w-auto"
        onClick={submit}
        disabled={!canSubmit}
      >
        {saga.isBusy ? (
          <>
            <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />
            {saga.phase === 'submitting' ? 'Submitting…' : 'Applying…'}
          </>
        ) : (
          <>
            <GitBranch className="mr-1.5 h-4 w-4" />
            Change reporting line
          </>
        )}
      </Button>
    </div>
  );
};

export default ReportingLinePanel;
